import React, { useContext, useState } from 'react';

import { ACTIVITY_TYPE } from 'Constants/project_plan';

import OwnerChip from './OwnerChip';

import UserSelectorInput from 'CommonComponents/UserSelectorInput';

import ProjectPlanContext from 'Context/project_plan/ProjectPlanContext';

import { useToast } from 'Hooks/useToast';
import { useUsers } from 'Hooks/useUsers';

/**
 * The Owner column is only as wide as the initials chip, but react-select's `menuPortalCSS`
 * default copies the *control's* width onto the portaled panel (`width: rect.width`), which
 * would leave every name in the picker clipped or wrapped over three lines.
 *
 * The override has to land on `menu`, not `menuPortal`: `UserSelectorInput` re-sets `menuPortal`
 * after spreading a caller's `styles`, so that one key can't be overridden — but `menu` survives
 * the merge. `minWidth` rather than `width` because react-select's own `menuCSS` sets
 * `width: '100%'`, and min-width beats width. The portal wrapper sets no `overflow`, so the
 * panel is free to paint wider than the column it hangs from.
 */
const OWNER_MENU_MIN_WIDTH = 220;

const ownerSelectStyles = {
    menu: (base) => ({ ...base, minWidth: OWNER_MENU_MIN_WIDTH }),
};

/** `rowId` is the id of the row's own record — a ProjectActivity id on activity rows, a ProjectCategory id otherwise. */
const OwnerCell = ({ rowId, type, name, ownerId = null }) => {
    const [editable, setEditable] = useState(false);
    const [userId, setUserId] = useState(ownerId);

    const { updateProjectCategoryOwner, updateProjectActivityOwner } = useContext(ProjectPlanContext);

    const { users } = useUsers();

    const toast = useToast();

    const handleBlur = () => {
        // Activity and category owners live on different models, so the row type decides
        // which endpoint is called — the ids are not interchangeable.
        const updateOwner = type === ACTIVITY_TYPE ? updateProjectActivityOwner : updateProjectCategoryOwner;

        // Explicit null so clearing the selection reaches the backend as a value to unset,
        // rather than relying on an undefined key being dropped from the request body.
        updateOwner(rowId, userId ?? null)
            .then(() => {
                setEditable(false);
                toast.success($t('Owner updated successfully'));
            })
            .catch((e) => {
                toast.error(e.response?.data?.message || $t('Failed to update owner'));
            });
    };

    if (editable) {
        return (
            <UserSelectorInput
                selectedId={userId}
                usersList={users}
                fetchIfNoUsers={false}
                onChange={(value) => setUserId(value)}
                onBlur={handleBlur}
                styles={ownerSelectStyles}
                ghost
            />
        );
    }

    return (
        <div className="tw-truncate tw-w-full tw-h-full tw-flex tw-items-center" onDoubleClick={() => setEditable(true)}>
            <OwnerChip name={name} ownerId={ownerId} />
        </div>
    );
};

export default OwnerCell;
